import {
  CreatePaymentIntentRequestBody,
  StripePublishableKeyResponseBody,
  CreatePaymentMethodRequestBody,
  CreateSetupIntentRequestBody,
  CreatePaymentRequestChargeRequestBody,
  ConfirmPaymentChargeRequestBody,
  ConfirmPaymentIntentRequestBody,
  GetPaymentRequestBody,
} from 'QueryTypes';
import { AppQueryConfig, AppEntitiesState } from 'types';
import { PaymentsEntitiesState } from 'StoreTypes';
import { QueryConfig } from 'redux-query';

const urls = {
  confirmPaymentCharge(paymentChargeId: number): string {
    return `payment-charge/${paymentChargeId}/confirm/`;
  },
  confirmPaymentIntent(paymentIntentId: string): string {
    return `payment-intent/${paymentIntentId}/create-charge/`;
  },
  paymentIntent(practiceId: number): string {
    return `practice/${practiceId}/stripe-payment-intent/`;
  },
  paymentMethod(patientId: number): string {
    return `patient/${patientId}/payment-methods/`;
  },
  paymentRequest(paymentRequestId: number): string {
    return `payment-request/${paymentRequestId}/`;
  },
  paymentRequestCharge(paymentRequestId: number): string {
    return `payment-request/${paymentRequestId}/charge/`;
  },
  setupIntent(patientId: number): string {
    return `patient/${patientId}/payments/stripe-setupintent/`;
  },
  stripePublishableKey(): string {
    return 'payments/stripe-publishable-key';
  },
};

export const confirmPaymentChargeQuery = (
  paymentChargeId: number,
  body: ConfirmPaymentChargeRequestBody,
): AppQueryConfig => ({
  url: urls.confirmPaymentCharge(paymentChargeId),
  body,
});

/**
 * Query to create a payment intent in Stripe, via Elaton's backend.
 */
export const paymentIntentQuery = (
  practiceId: number,
  body: CreatePaymentIntentRequestBody,
): AppQueryConfig => ({
  url: urls.paymentIntent(practiceId),
  body,
});

/**
 * Query to create a patient liability and payment charge in Elation, based on a
 * successfully completed payment intent in Stripe.
 */
export const confirmPaymentIntentQuery = (
  paymentIntentId: string,
  body: ConfirmPaymentIntentRequestBody,
): AppQueryConfig => ({
  url: urls.confirmPaymentIntent(paymentIntentId),
  body,
});

export const paymentMethodQuery = (
  patientId: number,
  body: CreatePaymentMethodRequestBody,
): AppQueryConfig => ({
  url: urls.paymentMethod(patientId),
  body,
});

export const paymentRequestQuery = (
  paymentRequestId: number,
  body: GetPaymentRequestBody,
): QueryConfig => ({
  url: urls.paymentRequest(paymentRequestId),
  body,
  force: true,
});

export const paymentRequestChargeQuery = (
  paymentRequestId: number,
  body: CreatePaymentRequestChargeRequestBody,
): AppQueryConfig => ({
  url: urls.paymentRequestCharge(paymentRequestId),
  body,
});

export const setupIntentQuery = (
  patientId: number,
  body: CreateSetupIntentRequestBody,
): AppQueryConfig => ({
  url: urls.setupIntent(patientId),
  body,
});

/**
 * Query to fetch our Stripe integration's publishable key.
 */
export const stripePublishableKeyQuery = (): AppQueryConfig => ({
  url: urls.stripePublishableKey(),
  transform: (
    responseJson: StripePublishableKeyResponseBody,
  ): Pick<AppEntitiesState, 'payments'> => ({
    payments: {
      stripePublishableKey: responseJson.publishableKey,
    },
  }),
  update: {
    payments: (prev, next): PaymentsEntitiesState => ({
      ...prev,
      stripePublishableKey: next.stripePublishableKey,
    }),
  },
});
